import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import propTypes from "@styled-system/prop-types";
import { useTranslation } from "react-i18next";
import { useController, useForm  } from "react-hook-form";
import { nanoid } from "nanoid";
import { pick, omit } from "lodash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp, faTimes } from "@fortawesome/pro-regular-svg-icons";
import {
  FormInputWrapper,
  FormLabelStyle,
  FormSelectStyle,
  FormMultiSelectStyle,
  FormMultiSelectedValueStyle,
  FormHelpStyle,
  FormInvalidFeedbackStyle,
} from "./forms.style";

const FormSelect = ({
  innerRef,
  validationRules,
  control: controlProp,
  formState: formStateProp,
  multiSelect,
  multiSelectConfig,
  plainText,
  inline,
  label,
  placeholder,
  type,
  name,
  defaultValue,
  options,
  valueProp,
  textProp,
  id,
  helpText,
  timeout,
  onSelect,
  onChange,
  onBlur,
  ...props
}) => {
  const { t } = useTranslation("components.global.multiSelect");
  const formHook = useForm();
  const control = controlProp || formHook.control;
  const formState = formStateProp || formHook.formState;
  const [isRequired] = useState(!!validationRules?.required);
  const [error, setError] = useState(formState?.errors[name] ?? undefined);
  const inputId = id || `select_${nanoid()}`;
  const spacePropsKeys = Object.keys(propTypes.space);
  const flexboxPropsKeys = Object.keys(propTypes.flexbox);
  const spaceProps = pick(props, spacePropsKeys);
  const flexboxProps = pick(props, flexboxPropsKeys);
  const attributes = omit(props, [...spacePropsKeys, ...flexboxPropsKeys]);
  const multiSelectOptions = useMemo(() => {
    if (!options) {
      return [];
    }

    return options.map((option) => ({
      label: option[textProp],
      value: option[valueProp],
      disabled: option.disabled,
    }));
  }, [options, textProp, valueProp]);
  const {
    field: { onChange: oc, onBlur: ob, value, ref: originalSelect },
    meta: { invalid },
  } = useController({
    name,
    control,
    rules: validationRules,
    defaultValue: defaultValue || (!multiSelect
      ? (placeholder ? "-1" : options.length ? options.find(o => !o.disabled)[valueProp] : "")
      : multiSelectOptions.length ? [multiSelectOptions.find(o => !o.disabled).value] : []),
  });
  const multiSelected = useMemo(() => {
    if (!multiSelect) {
      return [];
    }

    return multiSelectOptions.filter((option) => value.includes(option.value));
  }, [multiSelect, multiSelectOptions, value]);

  /* Set field error */
  useEffect(() => {
    if (!formState?.errors) {
      setError(undefined);
    } else if (formState.errors[name] !== error) {
      setError(formState.errors[name]);
    }
  }, [formState, name, error]);

  const onInputChange = (e) => {
    if (multiSelect) {
      const newVal = e.map((selected) => selected.value);
      oc(newVal);
      onChange && onChange(newVal);
    } else {
      oc(e);
      onChange && onChange(e);
    }
  };

  const onInputBlur = (e) => {
    ob();
    onBlur && onBlur(e);
  };

  return (
    <FormInputWrapper {...spaceProps} {...flexboxProps} ref={innerRef} inline={inline}>
      {label && <FormLabelStyle htmlFor={inputId}>{label}{isRequired && ` *`}</FormLabelStyle>}
      {multiSelect ? (
        <FormMultiSelectStyle
          name={name}
          id={inputId}
          labelledBy={placeholder}
          options={multiSelectOptions}
          value={multiSelected}
          onChange={onInputChange}
          onBlur={onInputBlur}
          overrideStrings={t("localizedStrings", { returnObjects: true })}
          {...multiSelectConfig}
          {...attributes}
        />
      ) : (
        <FormSelectStyle
          ref={originalSelect}
          name={name}
          id={inputId}
          value={value}
          invalid={invalid}
          onSelect={onSelect}
          onChange={onInputChange}
          onBlur={onInputBlur}
          {...attributes}
        >
          {placeholder && <option value="-1" disabled>{placeholder}</option>}
          {options.map((option) => (
            <option
              key={option[valueProp]}
              value={option[valueProp]}
              disabled={option.disabled}
            >{option[textProp]}</option>
          ))}
        </FormSelectStyle>
      )}
      {helpText && <FormHelpStyle>{helpText}</FormHelpStyle>}
      {error && <FormInvalidFeedbackStyle role="alert">{error.message}</FormInvalidFeedbackStyle>}
    </FormInputWrapper>
  );
};

FormSelect.propTypes = {
  innerRef: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  validationRules: PropTypes.object,
  watch: PropTypes.func,
  control: PropTypes.object,
  formState: PropTypes.object,
  multiSelect: PropTypes.bool,
  inline: PropTypes.bool,
  disabled: PropTypes.bool,
  label: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  name: PropTypes.string.isRequired,
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  options: PropTypes.array.isRequired,
  valueProp: PropTypes.string,
  textProp: PropTypes.string,
  id: PropTypes.string,
  placeholder: PropTypes.string,
  helpText: PropTypes.string,
  timeout: PropTypes.number,
  onSelect: PropTypes.func,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  multiSelectConfig: PropTypes.shape({
    hasSelectAll: PropTypes.bool,
    isLoading: PropTypes.bool,
    shouldToggleOnHover: PropTypes.bool,
    disableSearch: PropTypes.bool,
    valueRenderer: PropTypes.func,
    ItemRenderer: PropTypes.func,
    ClearIcon: PropTypes.node,
    ArrowRenderer: PropTypes.func,
    debounceDuration: PropTypes.number,
    ClearSelectedIcon: PropTypes.node,
    isCreatable: PropTypes.bool,
    onCreateOption: PropTypes.func,
  }),
  ...propTypes.space,
  ...propTypes.flexbox,
};

FormSelect.defaultProps = {
  multiSelect: false,
  inline: false,
  disabled: false,
  defaultValue: "",
  valueProp: "value",
  textProp: "text",
  multiSelectConfig: {
    ClearSelectedIcon: <FontAwesomeIcon icon={faTimes} style={{ marginRight: 10 }} />,
    ClearIcon: <FontAwesomeIcon icon={faTimes} />,
    ArrowRenderer: ({ expanded }) => {
      return expanded
        ? <FontAwesomeIcon icon={faChevronUp} />
        : <FontAwesomeIcon icon={faChevronDown} />;
    },
    valueRenderer: (selected, _options) => {
      return selected.length
        ? selected.map(({ label, value }) => (
            <FormMultiSelectedValueStyle key={value}>{label}</FormMultiSelectedValueStyle>
        ))
        : null;
    }
  },
};

export default FormSelect;
